import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { User, ArrowLeft, Heart } from 'lucide-react';
import { usePortalLogin, socialConnections } from '../../hooks/usePortalLogin';

export function CandidateLogin() {
  const { authConfigured, isPending, login: startLogin, register: startRegister, loginWithConnection } = usePortalLogin({
    portal: 'candidate',
    returnTo: '/candidate/dashboard',
  });

  const hasSocialLogins = socialConnections.length > 0;
  const resolveConnectionLabel = (connection: string) => {
    const value = connection.toLowerCase();
    if (value.includes('google')) return 'Continue with Google';
    if (value.includes('github')) return 'Continue with GitHub';
    if (value.includes('linkedin')) return 'Continue with LinkedIn';
    if (value.includes('microsoft') || value.includes('azure')) return 'Continue with Microsoft';
    return `Continue with ${connection}`;
  };

  const loginContent = (
    <div className="space-y-4 text-sm text-gray-300">
      <p className="text-gray-400">
        We use Auth0 across the platform so you can jump between candidate, employer, and admin areas with a single
        secure session. Continue to Auth0 and we&apos;ll bring you back to your dashboard.
      </p>
      {hasSocialLogins && (
        <div className="space-y-2">
          {socialConnections.map((connection: string) => (
            <Button
              key={connection}
              type="button"
              variant="outline"
              onClick={() => void loginWithConnection(connection)}
              disabled={isPending}
              className="w-full border-white/20 text-white hover:bg-white/10"
            >
              {resolveConnectionLabel(connection)}
            </Button>
          ))}
        </div>
      )}
      <Button
        type="button"
        onClick={() => void startLogin()}
        disabled={isPending || !authConfigured}
        className="w-full bg-gold-gradient text-black hover:brightness-110"
      >
        {authConfigured ? (isPending ? 'Redirecting...' : 'Continue with secure Auth0 login') : 'Auth0 not configured'}
      </Button>
      {!authConfigured && (
        <p className="text-xs text-red-300">
          Auth0 isn&apos;t configured. Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID to enable secure login.
        </p>
      )}
    </div>
  );

  const registerContent = authConfigured ? (
    <div className="space-y-4 text-sm text-gray-300">
      <p className="text-gray-400">
        Ready to create your profile? Auth0 powers signup too, so just continue and we&apos;ll launch the onboarding
        wizard once you&apos;re back.
      </p>
      <Button
        type="button"
        onClick={() => void startRegister()}
        disabled={isPending}
        className="w-full bg-gold-gradient text-black hover:brightness-110"
      >
        {isPending ? 'Opening secure signup...' : 'Create account with Auth0'}
      </Button>
      <p className="text-xs text-gray-500 text-center">
        By continuing you agree to our{' '}
        <Link to="/terms" className="text-[#ba9036] hover:underline">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="text-[#ba9036] hover:underline">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  ) : (
    <div className="space-y-3 text-sm text-gray-300">
      <p className="text-red-300">Auth0 isn&apos;t configured. Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID to enable signup.</p>
      <p className="text-gray-400">Once configured, you&apos;ll be redirected to Auth0 for secure signup.</p>
    </div>
  );

  return (
    <section
      aria-labelledby="candidate-login-heading"
      className="relative min-h-screen pt-16 flex items-center justify-center p-4 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <header className="text-center mb-8">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gold-gradient items-center justify-center mb-4">
            <User className="w-8 h-8 text-black" />
          </div>
          <h1 id="candidate-login-heading" className="mb-2 text-gold-gradient">
            Candidate Portal
          </h1>
          <p className="text-gray-400">Find your dream job today</p>
        </header>

        <Tabs defaultValue="login" className="w-full" aria-label="Candidate authentication">
          <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10">
            <TabsTrigger value="login" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black">
              Login
            </TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black">
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Welcome Back</CardTitle>
                <CardDescription className="text-gray-400">
                  Sign in to continue your job search
                </CardDescription>
              </CardHeader>
              <CardContent>{loginContent}</CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Join Lighthouse</CardTitle>
                <CardDescription className="text-gray-400">
                  Create your returner profile and connect with employers
                </CardDescription>
              </CardHeader>
              <CardContent>{registerContent}</CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8 space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-gray-300">
          <div className="flex items-center gap-3">
            <Heart className="w-4 h-4 text-[#ba9036]" />
            <span>Your data is secured with enterprise authentication.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

